/* stripe-generator.js - Streifenbauer.

   Generates a striping pattern from a set of colors with weighted
   distribution and a configurable mix of stripe heights. Calculates
   realistic yarn requirements from gauge + width + skein specs.

   Algorithm ported from the standalone Next.js bonifaktur-tools app
   (src/lib/stripe-generator.ts). Pure JS, no dependencies. State is
   persisted to localStorage; export uses the native Canvas API for PNG
   download and window.print() for printing.
*/
(function () {
  'use strict';

  const root = document.querySelector('[data-sg-app]');
  if (!root) return;

  const STATE_KEY = 'bonifaktur:sg:state:v1';

  // ── Config state ────────────────────────────────────────────────────────

  const DEFAULT_COLORS = ['#5A3A2E', '#C39E54', '#F3EFE8', '#3A4A4D'];

  const state = {
    colors: DEFAULT_COLORS.map(hex => ({ hex, quantity: 1 })),
    totalRows: 100,
    blockSizes: [2, 3, 4],
    symmetrical: false,
    gauge: 20,
    width: 50,
    yardage: 200,
    weight: 100,
    name: 'Mein Streifenmuster',
    pattern: null, // generated pattern { stripes, totalRows }
  };

  // Apply persisted state up-front so the bindings below initialise the DOM
  // from the *loaded* state, not the defaults above. Otherwise a saved
  // blockSizes/totalRows/etc. would diverge from what the UI shows.
  load();

  // ── DOM refs ────────────────────────────────────────────────────────────

  const colorsEl     = root.querySelector('[data-sg-colors]');
  const colorCountEl = root.querySelector('[data-sg-color-count]');
  const addColorBtn  = root.querySelector('[data-sg-add-color]');
  const totalRowsEl  = root.querySelector('[data-sg-total-rows]');
  const blockEls     = root.querySelectorAll('[data-sg-block]');
  const symEl        = root.querySelector('[data-sg-symmetrical]');
  const gaugeEl      = root.querySelector('[data-sg-gauge]');
  const widthEl      = root.querySelector('[data-sg-width]');
  const yardEl       = root.querySelector('[data-sg-yardage]');
  const weightEl     = root.querySelector('[data-sg-weight]');
  const generateBtn  = root.querySelector('[data-sg-generate]');
  const resetBtn     = root.querySelector('[data-sg-reset]');
  const emptyEl      = root.querySelector('[data-sg-empty]');
  const outputEl     = root.querySelector('[data-sg-output]');
  const nameEl       = root.querySelector('[data-sg-name]');
  const regenBtn     = root.querySelector('[data-sg-regenerate]');
  const pngBtn       = root.querySelector('[data-sg-png]');
  const printBtn     = root.querySelector('[data-sg-print]');
  const patternEl    = root.querySelector('[data-sg-pattern]');
  const axisEl       = root.querySelector('[data-sg-axis]');
  const reqTable     = root.querySelector('[data-sg-requirements]');
  const noticeEl     = root.querySelector('[data-sg-notice]');

  // ── Persistence ─────────────────────────────────────────────────────────

  function save() {
    try { localStorage.setItem(STATE_KEY, JSON.stringify(state)); }
    catch (_) { /* ignore */ }
  }

  function load() {
    try {
      const raw = localStorage.getItem(STATE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        Object.assign(state, parsed);
        // Defensive: ensure colors array exists
        if (!Array.isArray(state.colors) || !state.colors.length) {
          state.colors = DEFAULT_COLORS.map(hex => ({ hex, quantity: 1 }));
        }
      }
    } catch (_) { /* ignore */ }
  }

  // ── Color list management ───────────────────────────────────────────────

  function renderColors() {
    if (!colorsEl) return;
    colorsEl.innerHTML = '';
    state.colors.forEach((c, idx) => {
      const li = document.createElement('li');
      li.className = 'sg-color';

      // Swatch button (wraps a hidden color input)
      const swatch = document.createElement('label');
      swatch.className = 'sg-color__swatch';
      swatch.style.background = c.hex;
      swatch.setAttribute('aria-label', bfT('color_change_aria', 'Farbe ändern'));
      const colorInput = document.createElement('input');
      colorInput.type = 'color';
      colorInput.value = c.hex;
      colorInput.addEventListener('input', (e) => {
        const v = e.target.value;
        state.colors[idx].hex = v;
        swatch.style.background = v;
        hexInput.value = v.toUpperCase();
        save();
      });
      swatch.appendChild(colorInput);

      // Hex input (text field, allows direct typing)
      const hexInput = document.createElement('input');
      hexInput.type = 'text';
      hexInput.className = 'sg-color__hex';
      hexInput.value = c.hex.toUpperCase();
      hexInput.maxLength = 7;
      hexInput.spellcheck = false;
      hexInput.addEventListener('change', () => {
        const v = normalizeHex(hexInput.value);
        if (v) {
          state.colors[idx].hex = v;
          swatch.style.background = v;
          colorInput.value = v;
          hexInput.value = v.toUpperCase();
          save();
        } else {
          hexInput.value = c.hex.toUpperCase();
        }
      });

      // Quantity input - weighted proportion
      const qty = document.createElement('input');
      qty.type = 'number';
      qty.className = 'sg-color__qty';
      qty.min = '1';
      qty.max = '20';
      qty.value = String(c.quantity);
      qty.title = bfT('qty_share_title', 'Mengenanteil - höhere Zahl, mehr Reihen');
      qty.addEventListener('change', () => {
        const v = Math.max(1, parseInt(qty.value, 10) || 1);
        state.colors[idx].quantity = v;
        qty.value = String(v);
        save();
      });

      // Remove button
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'sg-color__remove';
      remove.setAttribute('aria-label', bfT('color_remove_aria', 'Farbe entfernen'));
      remove.innerHTML = '&times;';
      remove.addEventListener('click', () => {
        state.colors.splice(idx, 1);
        save();
        renderColors();
      });

      li.append(swatch, hexInput, qty, remove);
      colorsEl.appendChild(li);
    });

    if (colorCountEl) {
      const n = state.colors.length;
      colorCountEl.textContent = n === 1 ? bfT('color_count_one', '1 Farbe') : bfT('color_count_other', '{count} Farben').replace('{count}', n);
    }
  }

  function normalizeHex(input) {
    const v = String(input || '').trim().replace(/^#?/, '#');
    if (/^#[0-9a-fA-F]{3}$/.test(v)) {
      // Expand short form
      return '#' + v.slice(1).split('').map(c => c + c).join('').toLowerCase();
    }
    if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
    return null;
  }

  if (addColorBtn) {
    addColorBtn.addEventListener('click', () => {
      // Pick a default that doesn't collide with the last color.
      const candidates = ['#A86B5C', '#D7C2A2', '#7E8B86', '#2C2A28', '#B33A3A', '#4F6D7A'];
      const used = new Set(state.colors.map(c => c.hex));
      const pick = candidates.find(c => !used.has(c)) || candidates[0];
      state.colors.push({ hex: pick, quantity: 1 });
      save();
      renderColors();
    });
  }

  // ── Pattern settings UI bindings ────────────────────────────────────────

  function bindSetting(el, key, parser) {
    if (!el) return;
    el.value = state[key];
    el.addEventListener('change', () => {
      const v = parser ? parser(el.value) : el.value;
      state[key] = v;
      save();
    });
  }
  bindSetting(totalRowsEl, 'totalRows', v => Math.max(10, parseInt(v, 10) || 100));
  bindSetting(gaugeEl,     'gauge',     v => Math.max(5, parseInt(v, 10) || 20));
  bindSetting(widthEl,     'width',     v => Math.max(5, parseInt(v, 10) || 50));
  bindSetting(yardEl,      'yardage',   v => Math.max(20, parseInt(v, 10) || 200));
  bindSetting(weightEl,    'weight',    v => Math.max(10, parseInt(v, 10) || 100));

  blockEls.forEach(cb => {
    cb.checked = state.blockSizes.includes(parseInt(cb.value, 10));
    cb.addEventListener('change', () => {
      const v = parseInt(cb.value, 10);
      const setBlocks = new Set(state.blockSizes);
      if (cb.checked) setBlocks.add(v); else setBlocks.delete(v);
      state.blockSizes = [...setBlocks].sort((a, b) => a - b);
      save();
    });
  });

  if (symEl) {
    symEl.checked = !!state.symmetrical;
    symEl.addEventListener('change', () => {
      state.symmetrical = symEl.checked;
      save();
    });
  }

  if (nameEl) {
    nameEl.value = state.name;
    nameEl.addEventListener('change', () => {
      state.name = nameEl.value || 'Streifenmuster';
      save();
    });
  }

  // ── Algorithm (ported from stripe-generator.ts) ─────────────────────────

  function generateStripePattern(colors, settings) {
    if (!colors.length) throw new Error('Mindestens eine Farbe nötig.');
    if (!settings.blockSizes.length) throw new Error('Mindestens eine Streifenhöhe nötig.');

    const totalRows = Math.max(1, settings.totalRows);
    let blocks = [];
    let remaining = totalRows;
    let lastColor = null;

    const heightsUsed = new Set();
    const totalQty = colors.reduce((s, c) => s + c.quantity, 0);
    const usage = Object.fromEntries(colors.map(c => [c.hex, 0]));

    while (remaining > 0) {
      let candidates = colors.filter(c => c.hex !== lastColor);
      if (!candidates.length) candidates = colors;

      const pick = chooseNextColor(candidates, usage, totalQty);
      const size = chooseBlockSize(settings.blockSizes, remaining, heightsUsed);

      blocks.push({ color: pick.hex, rows: size });
      remaining -= size;
      lastColor = pick.hex;
      usage[pick.hex] += size;
      heightsUsed.add(size);
      if (heightsUsed.size === settings.blockSizes.length) heightsUsed.clear();
    }

    if (settings.symmetrical) blocks = makeSymmetrical(blocks, totalRows);
    return { stripes: blocks, totalRows };
  }

  function chooseNextColor(available, usage, totalQty) {
    const totalUsage = Object.values(usage).reduce((a, b) => a + b, 1);
    const options = available.map(color => ({
      color,
      deficit: (color.quantity / totalQty) - (usage[color.hex] / totalUsage),
    }));
    options.sort((a, b) => b.deficit - a.deficit);
    // 15% chance to pick second-best for variety
    const idx = Math.random() < 0.15 ? Math.min(1, options.length - 1) : 0;
    return options[idx].color;
  }

  function chooseBlockSize(sizes, remaining, used) {
    const unused = sizes.filter(s => !used.has(s) && s <= remaining);
    if (unused.length) return unused[Math.floor(Math.random() * unused.length)];
    const valid = sizes.filter(s => s <= remaining);
    if (valid.length) return valid[Math.floor(Math.random() * valid.length)];
    return Math.min(Math.min(...sizes), remaining);
  }

  function makeSymmetrical(blocks, totalRows) {
    const rowSum = blocks.reduce((s, b) => s + b.rows, 0);
    let acc = 0, mid = -1;
    for (let i = 0; i < blocks.length; i++) {
      acc += blocks[i].rows;
      if (acc >= rowSum / 2) { mid = i; break; }
    }
    if (mid === -1) return blocks;
    const first = blocks.slice(0, mid + 1);
    const second = rowSum % 2 === 0
      ? [...first].reverse()
      : [...first.slice(0, -1)].reverse();
    return [...first, ...second];
  }

  function calculateRequirements(pattern, calc) {
    const totals = {};
    pattern.stripes.forEach(s => { totals[s.color] = (totals[s.color] || 0) + s.rows; });

    // Approximate model: rowLengthCm = width × stitchesPerCm × consumption
    const stitchesPerCm = 5;
    const consumption = 2.2;
    const safety = 1.1;
    const rowLengthM = (calc.width * stitchesPerCm * consumption) / 100;
    const yardM = calc.yardage; // we treat user input as meters directly
    const metersPerGram = yardM / calc.weight;

    return Object.entries(totals).map(([color, rows]) => {
      const lenM = rows * rowLengthM * safety;
      const wG = lenM / metersPerGram;
      return {
        color,
        rows,
        percentage: (rows / pattern.totalRows) * 100,
        length: Math.round(lenM),
        weight: Math.round(wG),
        skeins: Math.ceil(wG / calc.weight),
      };
    }).sort((a, b) => b.rows - a.rows);
  }

  // ── Rendering ───────────────────────────────────────────────────────────

  function renderPattern() {
    if (!patternEl || !state.pattern) return;
    patternEl.innerHTML = '';
    state.pattern.stripes.forEach((stripe, i) => {
      const div = document.createElement('div');
      div.className = 'sg-stripe';
      div.style.background = stripe.color;
      div.style.setProperty('--sg-flex', String(stripe.rows));
      const txt = stripe.rows >= 4 ? `${stripe.rows} R.` : '';
      div.innerHTML = txt ? `<span class="sg-stripe__count">${txt}</span>` : '';
      patternEl.appendChild(div);
    });

    renderAxis();
  }

  function renderAxis() {
    if (!axisEl || !state.pattern) return;
    axisEl.innerHTML = '';
    const total = state.pattern.totalRows;
    // Marks every ~10 rows depending on total
    const step = total > 200 ? 50 : total > 100 ? 25 : 10;
    for (let r = step; r <= total; r += step) {
      const mark = document.createElement('div');
      mark.className = 'sg-axis-mark';
      mark.style.top = (r / total * 100) + '%';
      mark.textContent = `${r} R.`;
      axisEl.appendChild(mark);
    }
  }

  function renderRequirements() {
    if (!reqTable || !state.pattern) return;
    const tbody = reqTable.querySelector('tbody');
    if (!tbody) return;
    tbody.innerHTML = '';
    const reqs = calculateRequirements(state.pattern, {
      gauge: state.gauge,
      width: state.width,
      yardage: state.yardage,
      weight: state.weight,
    });
    reqs.forEach(r => {
      const tr = document.createElement('tr');
      tr.innerHTML =
        `<td><span class="sg-req-swatch" style="background: ${escapeHtml(r.color)};"></span></td>` +
        `<td>${escapeHtml(r.color.toUpperCase())}</td>` +
        `<td class="sg-num">${r.rows}</td>` +
        `<td class="sg-num">${r.percentage.toFixed(1)} %</td>` +
        `<td class="sg-num">${r.length} m</td>` +
        `<td class="sg-num">${r.weight} g</td>` +
        `<td class="sg-num">${r.skeins}</td>`;
      tbody.appendChild(tr);
    });
  }

  function escapeHtml(s) {
    const d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  // ── Generate handler ───────────────────────────────────────────────────

  function generate() {
    if (!state.colors.length) {
      showNotice(bfT('err_add_color', 'Bitte mindestens eine Farbe anlegen.'), { error: true });
      return;
    }
    if (!state.blockSizes.length) {
      showNotice(bfT('err_min_stripe', 'Bitte mindestens eine Streifenhöhe wählen.'), { error: true });
      return;
    }
    try {
      state.pattern = generateStripePattern(state.colors, {
        totalRows: state.totalRows,
        blockSizes: state.blockSizes,
        symmetrical: state.symmetrical,
      });
      save();
      showResult();
      renderPattern();
      renderRequirements();
    } catch (err) {
      console.warn('[stripe-generator] generation failed', err);
      showNotice(bfT('stripe_gen_error', 'Muster konnte nicht erzeugt werden: ') + err.message, { error: true });
    }
  }

  function showResult() {
    if (emptyEl) emptyEl.hidden = true;
    if (outputEl) outputEl.hidden = false;
  }

  function reset() {
    state.pattern = null;
    save();
    if (emptyEl) emptyEl.hidden = false;
    if (outputEl) outputEl.hidden = true;
  }

  if (generateBtn) generateBtn.addEventListener('click', generate);
  if (regenBtn)    regenBtn.addEventListener('click', generate);
  if (resetBtn)    resetBtn.addEventListener('click', reset);

  // ── PNG export ──────────────────────────────────────────────────────────

  if (pngBtn) {
    pngBtn.addEventListener('click', async () => {
      if (!state.pattern) return;
      const span = pngBtn.querySelector('span');
      const original = span ? span.textContent : '';
      if (span) span.textContent = bfT('generating', 'Wird erzeugt …');
      pngBtn.disabled = true;
      try {
        const dataUrl = renderPatternPng();
        const date = new Date().toISOString().slice(0, 10);
        const safeName = (state.name || 'streifen').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'streifen';
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `${safeName}-${date}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (err) {
        console.warn('[stripe-generator] export failed', err);
        showNotice(bfT('img_gen_failed', 'Bild konnte nicht erzeugt werden.'), { error: true });
      } finally {
        if (span) span.textContent = original;
        pngBtn.disabled = false;
      }
    });
  }

  function renderPatternPng() {
    const W = 800;
    const H = 1200;
    const TITLE_H = 60;
    const PAD = 40;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const canvas = document.createElement('canvas');
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    // Paper background
    const paper = (getComputedStyle(document.documentElement)
      .getPropertyValue('--paper').trim()) || '#F3EFE8';
    ctx.fillStyle = paper;
    ctx.fillRect(0, 0, W, H);

    // Title
    ctx.fillStyle = '#1B1A18';
    ctx.font = '500 28px "Times New Roman", Georgia, serif';
    ctx.textBaseline = 'top';
    ctx.fillText(state.name || 'Streifenmuster', PAD, PAD);
    ctx.font = '12px "SFMono-Regular", Menlo, monospace';
    ctx.fillStyle = '#6F6A63';
    ctx.fillText(
      bfT('stripe_summary', '{rows} Reihen · {colors} Farben').replace('{rows}', state.pattern.totalRows).replace('{colors}', state.colors.length),
      PAD, PAD + 38
    );

    // Pattern strip
    const stripX = PAD;
    const stripY = PAD + TITLE_H + 30;
    const stripW = (W - PAD * 2) * 0.70;
    const stripH = H - stripY - PAD - 100; // leave room for table

    let yCursor = stripY;
    state.pattern.stripes.forEach(s => {
      const rowH = (s.rows / state.pattern.totalRows) * stripH;
      ctx.fillStyle = s.color;
      ctx.fillRect(stripX, yCursor, stripW, rowH);
      // Row label
      if (rowH >= 14) {
        ctx.fillStyle = '#FFFFFF';
        ctx.font = '11px "SFMono-Regular", Menlo, monospace';
        ctx.textBaseline = 'middle';
        ctx.shadowColor = 'rgba(0,0,0,0.5)';
        ctx.shadowBlur = 2;
        ctx.fillText(`${s.rows} R.`, stripX + 8, yCursor + rowH / 2);
        ctx.shadowBlur = 0;
      }
      yCursor += rowH;
    });
    // Strip border
    ctx.strokeStyle = '#D9D2C5';
    ctx.lineWidth = 1;
    ctx.strokeRect(stripX, stripY, stripW, stripH);

    // Yarn requirements box on the right
    const tableX = stripX + stripW + 24;
    const tableW = W - PAD - tableX;
    let tableY = stripY;

    ctx.fillStyle = '#1B1A18';
    ctx.font = '600 11px "SFMono-Regular", Menlo, monospace';
    ctx.textBaseline = 'top';
    ctx.fillText('GARNBEDARF', tableX, tableY);
    tableY += 24;

    const reqs = calculateRequirements(state.pattern, {
      gauge: state.gauge, width: state.width,
      yardage: state.yardage, weight: state.weight,
    });
    reqs.forEach(r => {
      // Color swatch
      ctx.fillStyle = r.color;
      ctx.beginPath();
      ctx.arc(tableX + 9, tableY + 9, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#D9D2C5';
      ctx.stroke();

      ctx.fillStyle = '#1B1A18';
      ctx.font = '11px "SFMono-Regular", Menlo, monospace';
      ctx.textBaseline = 'top';
      ctx.fillText(r.color.toUpperCase(), tableX + 26, tableY);
      ctx.fillStyle = '#6F6A63';
      ctx.fillText(`${r.rows} R · ${r.percentage.toFixed(0)}%`, tableX + 26, tableY + 14);
      ctx.fillText(`${r.length} m · ${r.weight} g · ${r.skeins} ${r.skeins === 1 ? bfT('skein_one', 'Strang') : bfT('skein_other', 'Stränge')}`, tableX + 26, tableY + 28);
      tableY += 56;
    });

    // Footer
    ctx.fillStyle = '#6F6A63';
    ctx.font = '10px "SFMono-Regular", Menlo, monospace';
    ctx.textBaseline = 'bottom';
    const today = new Date().toLocaleDateString('de-DE');
    ctx.fillText(`bonifaktur.de · streifenbauer · ${today}`, PAD, H - PAD);

    return canvas.toDataURL('image/png');
  }

  // Open a self-contained popup window with just the pattern + table and
  // trigger the browser print dialog from there. Lets the user pick "Save
  // as PDF" or print to paper without us having to ship a PDF library;
  // the popup has none of the theme chrome or third-party widgets, so the
  // output is a clean single-page A4 every time.
  function openPrintWindow() {
    if (!state.pattern) return;

    const reqs = calculateRequirements(state.pattern, {
      gauge: state.gauge, width: state.width,
      yardage: state.yardage, weight: state.weight,
    });

    const esc = s => String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#039;');

    const stripesHtml = state.pattern.stripes.map(s => {
      const flex = s.rows;
      // Inline color: keep a contrast-aware label colour
      const lum = (() => {
        const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(s.color || '');
        if (!m) return 0;
        const [r, g, b] = [m[1], m[2], m[3]].map(h => parseInt(h, 16));
        return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      })();
      const fg = lum > 0.6 ? '#1B1A18' : '#fff';
      return `<div class="stripe" style="flex:${flex};background:${esc(s.color)};color:${fg};">
        <span>${s.rows} R.</span>
      </div>`;
    }).join('');

    const reqsHtml = reqs.map(r => `
      <tr>
        <td><span class="sw" style="background:${esc(r.color)};"></span></td>
        <td class="hex">${esc(r.color.toUpperCase())}</td>
        <td class="num">${r.rows}</td>
        <td class="num">${r.percentage.toFixed(0)}%</td>
        <td class="num">${r.length} m</td>
        <td class="num">${r.weight} g</td>
        <td class="num">${r.skeins}</td>
      </tr>`).join('');

    const today = new Date().toLocaleDateString('de-DE');
    const totalRows = state.pattern.totalRows;
    const colorCount = state.colors.length;
    const projectName = state.name || 'Streifenmuster';
    const widthCm = state.width;

    const doc = `<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="utf-8">
<title>${esc(projectName)} · BONIFAKTUR Streifenbauer</title>
<style>
  @page { size: A4; margin: 14mm 12mm; }
  *, *::before, *::after {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    box-sizing: border-box;
  }
  html, body { margin: 0; padding: 0; background: #fff; color: #1B1A18; font-family: 'Source Sans 3', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif; }
  body { padding: 0; }
  .head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 6mm; padding-bottom: 3mm; border-bottom: 0.5pt solid #C9C2B5; }
  .title { font-family: 'Schibsted Grotesk', sans-serif; font-size: 18pt; font-weight: 400; margin: 0; letter-spacing: -0.005em; }
  .meta { font-family: 'Courier Prime', 'Courier New', monospace; font-size: 9pt; color: #6F6A63; letter-spacing: 0.04em; text-transform: uppercase; }
  .layout { display: grid; grid-template-columns: 1fr 18mm; gap: 4mm; margin-bottom: 6mm; page-break-inside: avoid; }
  .pattern { display: flex; flex-direction: column; height: 165mm; border: 0.5pt solid #999; overflow: hidden; }
  .stripe { display: flex; align-items: center; justify-content: flex-end; padding-right: 3mm; min-height: 1.6mm; font-family: 'Courier Prime', 'Courier New', monospace; font-size: 7pt; letter-spacing: 0.04em; }
  .stripe span { text-shadow: 0 0.5pt 1pt rgba(0,0,0,0.4); }
  .axis { position: relative; font-family: 'Courier Prime', 'Courier New', monospace; font-size: 7pt; color: #444; border-left: 0.5pt solid #999; }
  .axis-mark { position: absolute; left: 2mm; transform: translateY(-50%); white-space: nowrap; }
  h2 { font-family: 'Schibsted Grotesk', sans-serif; font-size: 11pt; font-weight: 400; margin: 0 0 3mm; letter-spacing: 0.04em; text-transform: uppercase; color: #444; }
  table { width: 100%; border-collapse: collapse; font-size: 9pt; }
  th, td { padding: 1.5mm 2mm; border-bottom: 0.5pt solid #DCD4C6; text-align: left; vertical-align: middle; }
  th { font-family: 'Courier Prime', 'Courier New', monospace; font-size: 7pt; letter-spacing: 0.08em; text-transform: uppercase; color: #6F6A63; font-weight: 400; }
  td.num, th.num { text-align: right; font-family: 'Courier Prime', 'Courier New', monospace; }
  td.hex { font-family: 'Courier Prime', 'Courier New', monospace; font-size: 8pt; }
  .sw { display: inline-block; width: 4mm; height: 4mm; border-radius: 50%; border: 0.5pt solid #555; vertical-align: middle; }
  .foot { margin-top: 6mm; padding-top: 3mm; border-top: 0.5pt solid #DCD4C6; font-family: 'Courier Prime', 'Courier New', monospace; font-size: 7pt; color: #6F6A63; letter-spacing: 0.06em; text-transform: uppercase; display: flex; justify-content: space-between; }
  @media screen {
    body { background: #f4f1ea; padding: 8mm; }
    .sheet { background: #fff; padding: 14mm 12mm; max-width: 210mm; margin: 0 auto; box-shadow: 0 2px 18px rgba(0,0,0,0.08); }
  }
  @media print {
    .no-print { display: none !important; }
    .sheet { box-shadow: none !important; padding: 0 !important; max-width: none !important; }
  }
</style>
</head>
<body>
<div class="sheet">
  <div class="head">
    <h1 class="title">${esc(projectName)}</h1>
    <span class="meta">${totalRows} Reihen · ${colorCount} Farben · ${widthCm} cm</span>
  </div>
  <div class="layout">
    <div class="pattern">${stripesHtml}</div>
    <div class="axis">
      ${(() => {
        const step = totalRows >= 200 ? 25 : totalRows >= 100 ? 20 : 10;
        const marks = [];
        for (let r = step; r < totalRows; r += step) {
          const top = (r / totalRows) * 100;
          marks.push(`<span class="axis-mark" style="top:${top}%">${r} R.</span>`);
        }
        return marks.join('');
      })()}
    </div>
  </div>
  <h2>Garnbedarf</h2>
  <table>
    <thead>
      <tr>
        <th></th><th>Farbe</th><th class="num">Reihen</th><th class="num">Anteil</th>
        <th class="num">Länge</th><th class="num">Gewicht</th><th class="num">Stränge</th>
      </tr>
    </thead>
    <tbody>${reqsHtml}</tbody>
  </table>
  <div class="foot">
    <span>bonifaktur.de · streifenbauer</span>
    <span>${today}</span>
  </div>
</div>
<script>
  // Print as soon as fonts/layout settle. Closing on afterprint lets users
  // who hit "Cancel" still keep the window open if they want.
  window.addEventListener('load', () => {
    setTimeout(() => window.print(), 60);
  });
  window.addEventListener('afterprint', () => window.close());
<\/script>
</body>
</html>`;

    const w = window.open('', '_blank', 'noopener,noreferrer');
    if (!w) {
      showNotice(bfT('err_popup_blocked', 'Popup-Blocker verhindert das PDF-Fenster. Bitte zulassen.'), { error: true });
      return;
    }
    w.document.open();
    w.document.write(doc);
    w.document.close();
  }

  if (printBtn) {
    printBtn.addEventListener('click', openPrintWindow);
  }

  // ── Notice helper ───────────────────────────────────────────────────────

  let noticeTimer = 0;
  function showNotice(message, opts) {
    if (!noticeEl) { console.warn('[stripe-generator]', message); return; }
    const isError = opts && opts.error;
    noticeEl.textContent = message;
    noticeEl.classList.toggle('sg-notice--error', !!isError);
    noticeEl.hidden = false;
    void noticeEl.offsetHeight;
    noticeEl.classList.add('is-visible');
    clearTimeout(noticeTimer);
    noticeTimer = setTimeout(() => {
      noticeEl.classList.remove('is-visible');
      setTimeout(() => { noticeEl.hidden = true; }, 220);
    }, (opts && opts.duration) || 3000);
  }

  // ── Init ────────────────────────────────────────────────────────────────

  renderColors();
  if (state.pattern) {
    showResult();
    renderPattern();
    renderRequirements();
  }
})();
